import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  actionsOnContentOwnership,
  membersRoles,
  USER_CHANGE_CONTENT_TYPE,
} from 'shared/const';

import { listMembers } from '../../PlaylistAdministration/context/graphql/queries';
import { updateMember } from './graphql/mutations';

export default function useUserRoles({
  handleHistory,
  isOrganizationAdmin,
  manageRoles,
  modifyContentStatus,
  organizationId,
  registeredMembers,
  requestToDeleteMemberGroups,
  requestToDeleteMemberPlaylists,
  requestToTransferUserGroups,
  requestToTransferUserPlaylists,
  setModifyContentStatus,
  useGraphQLMutation,
}) {
  const { t } = useTranslation();

  const [administrateUserRoleTo, setAdministrateUserRoleTo] = useState(null);
  const [administrateUserRoleOpen, setAdministrateUserRoleOpen] = useState(
    isOrganizationAdmin && manageRoles
  );
  const [changeRoleFormStatus, setChangeRoleFormStatus] = useState(null);
  const [modifyContentTarget, setModifyContentTarget] = useState({});
  const [updatedUserId, setUpdatedUserId] = useState();
  const [updatedMemberRole, setUpdatedMemberRole] = useState();

  const administrateManagersRoleOptions = [
    {
      value: membersRoles.admin,
      key: membersRoles.admin,
      label: t('administrateUsers.administrator'),
    },
    {
      value: membersRoles.manager,
      key: membersRoles.manager,
      label: t('administrateUsers.manager'),
    },
    {
      value: membersRoles.member,
      key: membersRoles.member,
      label: t('administrateUsers.member'),
    },
  ];

  const [updateMemberMutation] = useGraphQLMutation(updateMember, {
    onCompleted: ({ updateMember }) => {
      const selectedUser = registeredMembers.find(
        (member) => member.id === updateMember.id
      );
      if (selectedUser) {
        setAdministrateUserRoleTo(selectedUser);
      }

      setChangeRoleFormStatus({
        success: true,
        message: t('administrateUserRole.userRoleChangeFailed'),
      });
    },
    onError: () => {
      setChangeRoleFormStatus({
        success: false,
        message: t('administrateUserRole.userRoleChangeSuccess'),
      });
    },
    update(cache, { data: { updateMember } }) {
      const filter = {
        organizationId: { eq: organizationId },
        role: [membersRoles.admin, membersRoles.manager],
      };

      const existingData = cache.readQuery({
        query: listMembers,
        variables: { filter },
      });

      if (existingData) {
        const existingMember = existingData.listMembers.find(
          (member) => member.id === updateMember.id
        );
        let updatedMembers;
        if (existingMember && updateMember.role === membersRoles.member) {
          updatedMembers = existingData.listMembers.filter(
            (member) => member.id !== updateMember.id
          );
        } else if (
          !existingMember &&
          (updateMember.role === membersRoles.manager ||
            updateMember.role === membersRoles.admin)
        ) {
          updatedMembers = [
            ...existingData.listMembers,
            {
              id: updateMember.id,
              user: {
                id: updateMember.user.id,
                email: updateMember.user.email,
                name: updateMember.user.name,
              },
              // eslint-disable-next-line no-underscore-dangle
              __typename: updateMember.__typename,
            },
          ];
        }
        if (updatedMembers) {
          cache.writeQuery({
            query: listMembers,
            variables: { filter },
            data: { listMembers: updatedMembers },
          });
        }
      }
    },
  });

  const administrateUserRole = (user) => {
    const selectedUser = registeredMembers.find(
      (member) => member.user.id === user.id
    );
    if (selectedUser) {
      setAdministrateUserRoleTo(selectedUser);
    }
    setAdministrateUserRoleOpen(true);
    if (handleHistory) {
      if (selectedUser?.id) {
        handleHistory(`user=${selectedUser.id}&manage=role`);
      } else {
        handleHistory('manage=role');
      }
    }
  };

  const updateMemberHandler = (memberData) => {
    const { userId, role } = memberData ?? {};
    updateMemberMutation({
      variables: {
        input: {
          organizationId,
          role: role || updatedMemberRole,
          userId: userId || updatedUserId,
        },
      },
    });
  };

  const onSubmitAdministrateUserRoleHandler = (values) => {
    const { actionOnContent, groups, memberId, playlists, roleChangeTo } =
      values;

    const userId = registeredMembers.find((member) => member.id === memberId)
      .user.id;

    setUpdatedUserId(userId);
    setUpdatedMemberRole(roleChangeTo);
    setChangeRoleFormStatus();

    let type = '';

    if (actionOnContent === actionsOnContentOwnership.delete) {
      type = USER_CHANGE_CONTENT_TYPE.DELETE;
    } else if (actionOnContent === actionsOnContentOwnership.transfer) {
      type = USER_CHANGE_CONTENT_TYPE.TRANSFER;
    }

    const target = {
      groups: false,
      playlists: false,
      type,
    };
    const changed = { ...target };

    const actions = [];

    if (type === USER_CHANGE_CONTENT_TYPE.DELETE) {
      if (groups) {
        target.groups = true;
        actions.push(() => requestToDeleteMemberGroups(memberId, type));
      }
      if (playlists) {
        target.playlists = true;
        actions.push(() => requestToDeleteMemberPlaylists(memberId, type));
      }
    } else if (type === USER_CHANGE_CONTENT_TYPE.TRANSFER) {
      if (groups) {
        target.groups = true;
        actions.push(() =>
          requestToTransferUserGroups({ ...values, userId }, type)
        );
      }
      if (playlists) {
        target.playlists = true;
        actions.push(() =>
          requestToTransferUserPlaylists({ ...values, userId }, type)
        );
      }
    }

    setModifyContentStatus(changed);
    setModifyContentTarget(target);

    if (actions.length === 0) {
      updateMemberHandler({ userId, role: roleChangeTo });
    } else {
      actions.forEach((action) => action());
    }
  };

  useEffect(() => {
    if (modifyContentStatus?.type === USER_CHANGE_CONTENT_TYPE.DELETE) {
      if (modifyContentStatus.error) {
        setChangeRoleFormStatus({
          success: false,
          message: 'Failed to delete user content',
        });
      } else if (
        modifyContentTarget.groups === modifyContentStatus.groups &&
        modifyContentTarget.playlists === modifyContentStatus.playlists
      ) {
        updateMemberHandler();
      }
    } else if (
      modifyContentStatus?.type === USER_CHANGE_CONTENT_TYPE.TRANSFER
    ) {
      if (modifyContentStatus.error) {
        setChangeRoleFormStatus({
          success: false,
          message: 'Failed to transfer user content',
        });
      } else if (
        modifyContentTarget.groups === modifyContentStatus.groups &&
        modifyContentTarget.playlists === modifyContentStatus.playlists
      ) {
        updateMemberHandler();
      }
    }
  }, [modifyContentStatus]);

  return {
    administrateManagersRoleOptions,
    administrateUserRole,
    administrateUserRoleTo,
    administrateUserRoleOpen,
    changeRoleFormStatus,
    onSubmitAdministrateUserRoleHandler,
    setAdministrateUserRoleOpen,
    setChangeRoleFormStatus,
  };
}
